import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Tab,
    Tabs,
    Typography,
    Snackbar,
    Alert,
    Button
} from '@mui/material';
import DeliveryRequestTable from './DeliveryRequestTable';
import PresetDeliveryTable from './PresetDeliveryTable';
import MainCard from "../../components/cards/MainCard";
import { useGetPresetRequestsQuery } from '../../store/services/delivery-service';


function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`delivery-tabpanel-${index}`}
            aria-labelledby={`delivery-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

export default function DeliveryDashboard() {
    const [tabValue, setTabValue] = useState(0);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    // Selected delivery group for calendar view
    const [selectedDeliveryGroup, setSelectedDeliveryGroup] = useState(null);

    // Get user and tenancy information from Redux store
    const { tenancyId } = useSelector((state) => state.tenancy);
    const { role } = useSelector((state) => state.auth.user || {});
    const navigate = useNavigate();

    const isAdmin = role === "Administrators";

    const {
        data: customPresetData,
        error: customPresetError,
        isLoading: isCustomPresetLoading
    } = useGetPresetRequestsQuery({
        perPage: 100,
        page: 1,
        tenancyId: tenancyId
    });

    const {
        data: globalPresetData,
        error: globalPresetError,
        isLoading: isGlobalPresetLoading
    } = useGetPresetRequestsQuery({
        perPage: 100,
        page: 1,
        tenancyId: 0
    });

    const combinedPresetData = {
        presets: [
            ...(customPresetData?.presets || []),
            ...(globalPresetData?.presets || [])
        ],
        totalRecords: (customPresetData?.totalRecords || 0) + (globalPresetData?.totalRecords || 0)
    };

    const presetError = customPresetError || globalPresetError;
    const isPresetLoading = isCustomPresetLoading || isGlobalPresetLoading;


    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const showNotification = (message, severity = 'success') => {
        setSnackbar({
            open: true,
            message,
            severity
        });
    };

    const handleOpenCalendar = (deliveryGroup) => {
        setSelectedDeliveryGroup(deliveryGroup);
        navigate(`/delivery/calendar/${tenancyId}/${deliveryGroup.id}`);
    };

    // Check for notification from navigation state (e.g., after saving calendar)
    useEffect(() => {
        const location = window.location;
        if (location.state?.notification) {
            showNotification(
                location.state.notification.message,
                location.state.notification.severity || 'success'
            );

            // Clear the state to prevent showing notification again on refresh
            window.history.replaceState({}, document.title);
        }
    }, []);

    return (
        <>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={tabValue} onChange={handleTabChange} aria-label="delivery tabs">
                    <Tab label="Active" />
                    {isAdmin && <Tab label="Templates" />}
                </Tabs>
            </Box>

            <TabPanel value={tabValue} index={0}>
                <DeliveryRequestTable
                    tenancyId={tenancyId}
                    presetData={combinedPresetData}
                    showNotification={showNotification}
                    onViewCalendar={handleOpenCalendar}
                />
            </TabPanel>

            {isAdmin && (
                <TabPanel value={tabValue} index={1}>
                    <PresetDeliveryTable
                        presetData={combinedPresetData}
                        error={presetError}
                        isLoading={isPresetLoading}
                        showNotification={showNotification}
                        onViewCalendar={handleOpenCalendar}
                    />
                </TabPanel>
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
}